// eslint-disable-next-line import/no-cycle
import router from '@/router'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import Cookies from 'js-cookie'
import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // Prevent multiple session expired toasts from showing at the same time
  isAlreadyShowingExpiredToast = false

  // For Refreshing Token
  subscribers = []

  // Timer reference for proactive refresh
  refreshTimeoutId = null

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      async config => {
        const requestUrl = config.url || ''
        const isAbsoluteUrl = /^(https?:)?\/\//.test(requestUrl)

        if (isAbsoluteUrl || this.isRefreshRequest(config) || this.isAuthFreeRequest(config)) {
          return config
        }

        let refreshToken = this.getRefreshToken()
        let refreshExpiry = this.getRefreshTokenExpiry()

        let accessToken = this.getToken()
        if (!accessToken) {
          if (!refreshToken) {
            return config
          }

          if (this.isTokenExpired(refreshExpiry)) {
            this.invalidateSession()
            return config
          }

          try {
            accessToken = await this.queueTokenRefresh()
            if (!accessToken) {
              return config
            }

            refreshToken = this.getRefreshToken()
            refreshExpiry = this.getRefreshTokenExpiry()
          } catch (error) {
            return Promise.reject(error)
          }
        }

        if (refreshToken && this.isTokenExpired(refreshExpiry)) {
          this.invalidateSession()
          return config
        }

        const shouldRenewAccessToken = this.shouldRenewToken(this.getTokenExpiry())
        const shouldRenewRefreshToken = this.shouldRenewToken(refreshExpiry)

        if (refreshToken && (shouldRenewAccessToken || shouldRenewRefreshToken)) {
          try {
            const newAccessToken = await this.queueTokenRefresh()
            if (newAccessToken) {
              // eslint-disable-next-line no-param-reassign
              config.headers.Authorization = `${this.jwtConfig.tokenType} ${newAccessToken}`
            }
            return config
          } catch (error) {
            return Promise.reject(error)
          }
        }

        if (!config.headers) {
          // eslint-disable-next-line no-param-reassign
          config.headers = {}
        }

        if (!requestUrl.includes('https://')) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }

        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        // const { config, response: { status } } = error
        const { config, response } = error
        // eslint-disable-next-line no-unused-vars
        const originalRequest = config

        // if (status === 401) {
        if (response && response.status === 401) {
          // Always show Session Expired notification on 401 failures,
          // as long as there is no active toast showing.
          if (!this.isAlreadyShowingExpiredToast) {
            this.isAlreadyShowingExpiredToast = true

            Vue.prototype.$toast({
              component: ToastificationContent,
              props: {
                title: 'Sesi Berakhir',
                text: 'Sesi Anda telah berakhir, harap masuk untuk melanjutkan',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })

            // Reset the toast flag after 5 seconds
            setTimeout(() => {
              this.isAlreadyShowingExpiredToast = false
            }, 5000)
          }

          this.invalidateSession()

          // Return a pending Promise to ensure catch blocks down the chain
          // (e.g. actions showing generic "Error!") are never executed.
          return new Promise(() => { })
        }

        if (response && response.status === 403) {
          // Handle 403 errors for all endpoints
          Vue.prototype.$toast({
            component: ToastificationContent,
            props: {
              title: 'Akses Ditolak',
              text: 'Anda tidak memiliki akses. Harap hubungi Administrator.',
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
        } else if ((response && response.status === 408) || error.code === 'ECONNABORTED') {
          store.commit('appConfig/UPDATE_ERROR', true)
        } else if (response && response.status === 422) {
          if (!response.data.message) {
            Object.assign(response.data.errors, Object.fromEntries(
              Object.entries(response.data.errors)
                .map(
                  ([key, value]) => [key, value[0]],
                ),
            ))
          }
        } else {
          store.commit('appConfig/UPDATE_ERROR', true)
        }

        return Promise.reject(error)
      },
    )

    const existingAccessExpiry = this.getTokenExpiry()
    if (existingAccessExpiry) {
      this.scheduleAccessTokenRefresh(existingAccessExpiry)
    }
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach(callback => {
      callback(accessToken)
    })
    this.subscribers = []
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return Cookies.get(this.jwtConfig.storageTokenKeyName)
  }

  getTokenExpiry() {
    return Cookies.get(this.jwtConfig.storageTokenExpiryKeyName)
  }

  removeToken() {
    const options = this.buildRemovalOptions()
    Cookies.remove(this.jwtConfig.storageTokenKeyName, options)
    Cookies.remove(this.jwtConfig.storageRefreshTokenKeyName, options)
    Cookies.remove(this.jwtConfig.storageTokenExpiryKeyName, options)
    Cookies.remove(this.jwtConfig.storageRefreshTokenExpiryKeyName, options)
    this.clearAccessTokenRefreshTimer()
  }

  getRefreshToken() {
    return Cookies.get(this.jwtConfig.storageRefreshTokenKeyName)
  }

  getRefreshTokenExpiry() {
    return Cookies.get(this.jwtConfig.storageRefreshTokenExpiryKeyName)
  }

  setToken(value, expiresAt) {
    this.storeCookie(this.jwtConfig.storageTokenKeyName, value, expiresAt)
    this.storeExpiry(this.jwtConfig.storageTokenExpiryKeyName, expiresAt)
    this.scheduleAccessTokenRefresh(expiresAt)
  }

  setRefreshToken(value, expiresAt) {
    this.storeCookie(this.jwtConfig.storageRefreshTokenKeyName, value, expiresAt)
    this.storeExpiry(this.jwtConfig.storageRefreshTokenExpiryKeyName, expiresAt)
  }

  storeCookie(key, value, expiresAt) {
    if (!value) {
      this.clearCookie(key)
      return
    }

    const options = this.buildCookieOptions(expiresAt)
    Cookies.set(key, value, options)
  }

  storeExpiry(key, expiresAt) {
    if (!expiresAt) {
      this.clearCookie(key)
      return
    }

    const options = this.buildCookieOptions(expiresAt)
    Cookies.set(key, expiresAt, options)
  }

  clearCookie(key) {
    Cookies.remove(key, this.buildRemovalOptions())
  }

  buildCookieOptions(expiresAt) {
    const options = {
      secure: true, // Use HTTPS
      sameSite: 'lax',
    }

    const domain = this.getCookieDomain()
    if (domain) {
      options.domain = domain
    }

    const expiryDate = this.parseExpiry(expiresAt)
    if (expiryDate) {
      options.expires = expiryDate
    } else {
      options.expires = this.jwtConfig.cookieTTL || 30
    }

    return options
  }

  shouldRenewToken(expiry) {
    const expiryDate = this.parseExpiry(expiry)
    if (!expiryDate) return false

    const leeway = this.jwtConfig.tokenRenewalLeeway || 0
    return expiryDate.getTime() - leeway <= Date.now()
  }

  isTokenExpired(expiry) {
    const expiryDate = this.parseExpiry(expiry)
    if (!expiryDate) return false

    return expiryDate.getTime() <= Date.now()
  }

  getCookieDomain() {
    return this.jwtConfig.cookieDomain || process.env.VUE_APP_TOKEN_DOMAIN
  }

  buildRemovalOptions() {
    const domain = this.getCookieDomain()
    return domain ? { domain } : undefined
  }

  parseExpiry(expiry) {
    if (!expiry) return null

    const { tokenExpiryOffset = 0 } = this.jwtConfig || {}
    const date = new Date(expiry)
    if (Number.isNaN(date.getTime())) {
      return null
    }

    if (tokenExpiryOffset) {
      date.setMilliseconds(date.getMilliseconds() + tokenExpiryOffset)
    }

    return date
  }

  scheduleAccessTokenRefresh(expiresAt) {
    this.clearAccessTokenRefreshTimer()

    const expiryDate = this.parseExpiry(expiresAt)
    if (!expiryDate) {
      return
    }

    const leeway = this.jwtConfig.tokenRenewalLeeway || 0
    const delay = expiryDate.getTime() - Date.now()
    const scheduleDelay = delay - leeway

    if (scheduleDelay <= 0) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    this.refreshTimeoutId = window.setTimeout(() => {
      this.refreshTimeoutId = null
      this.triggerBackgroundRefresh()
    }, scheduleDelay)
  }

  clearAccessTokenRefreshTimer() {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  }

  triggerBackgroundRefresh() {
    const refreshPromise = this.queueTokenRefresh()

    if (refreshPromise && typeof refreshPromise.then === 'function') {
      refreshPromise.catch(() => { })
    }
  }

  isAuthFreeRequest(config) {
    const url = config.url || ''
    const publicEndpoints = [
      this.jwtConfig.loginEndpoint,
      this.jwtConfig.loginGoogleEndpoint,
      this.jwtConfig.registerEndpoint,
    ].filter(Boolean)

    return publicEndpoints.some(endpoint => url.includes(endpoint))
  }

  isRefreshRequest(config) {
    const url = config.url || ''
    return Boolean(this.jwtConfig.refreshEndpoint && url.includes(this.jwtConfig.refreshEndpoint))
  }

  async queueTokenRefresh() {
    if (!this.getRefreshToken()) {
      this.onAccessTokenFetched(null)
      this.invalidateSession()
      return null
    }

    if (!this.isAlreadyFetchingAccessToken) {
      this.isAlreadyFetchingAccessToken = true

      this.refreshToken()
        .then(response => {
          const payload = response?.data?.data || {}
          const {
            access_token: accessToken,
            refresh_token: refreshToken,
            access_token_expires_at: accessTokenExpiresAt,
            refresh_token_expires_at: refreshTokenExpiresAt,
          } = payload

          if (!accessToken) {
            throw new Error('Invalid refresh token response')
          }

          this.setToken(accessToken, accessTokenExpiresAt)
          if (refreshToken) {
            this.setRefreshToken(refreshToken, refreshTokenExpiresAt)
          }

          this.onAccessTokenFetched(accessToken)
          return accessToken
        })
        .catch(error => {
          this.onAccessTokenFetched(null)
          this.invalidateSession()
          throw error
        })
        .finally(() => {
          this.isAlreadyFetchingAccessToken = false
        })
    }

    return new Promise((resolve, reject) => {
      this.addSubscriber(token => {
        if (token) {
          resolve(token)
        } else {
          reject(new Error('Unable to refresh token'))
        }
        return false
      })
    })
  }

  invalidateSession() {
    if (this.subscribers.length) {
      this.onAccessTokenFetched(null)
    }

    this.removeToken()

    localStorage.removeItem('userData')

    const userData = {
      ability: initialAbility,
    }

    ability.update(initialAbility)
    store.commit('auth/UPDATE_USER_DATA', userData)

    const currentRoute = router.currentRoute || {}
    if (currentRoute.name !== 'login') {
      const redirectTo = currentRoute.fullPath || currentRoute.path || '/'
      router.replace({ name: 'login', query: { redirectTo } }).catch(() => { })
    }
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  loginGoogle(...args) {
    return this.axiosIns.post(this.jwtConfig.loginGoogleEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  async logout() {
    try {
      const refreshToken = this.getRefreshToken()
      if (this.jwtConfig.logoutEndpoint && refreshToken) {
        await this.axiosIns.post(this.jwtConfig.logoutEndpoint, {
          refresh_token: refreshToken,
        }).catch(() => null)
      }
    } finally {
      this.invalidateSession()
    }
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refresh_token: this.getRefreshToken(),
    })
  }
}
